import { useState } from 'react'
import './CallWidget.css'

// BigConnect voice widget configuration.
// The widget script renders its own launcher + call panel inside a shadow DOM
// and talks to the BigConnect calling API. We keep our own branded "talk to us"
// button and simply open the widget's panel when it's clicked, hiding the
// widget's default launcher so there aren't two buttons on screen.
const WIDGET_SCRIPT_ID = 'call-widget-script'
// Served through the same-origin "/widget" path (Vite proxy in dev, Nginx in
// prod) so the module + its relative chunks load without cross-origin issues.
const WIDGET_SRC = '/widget/widget.js'
const WIDGET_KEY = 'wk_7cb706ead81287a8f9799f174b50f53d'
// NOTE: we deliberately do NOT set data-api. The widget's calling API lives at
// its own baked-in endpoint (execute-api), not at the static "/widget" host, so
// overriding the API base to "/widget" would break call setup (404/405).

type ShadowHost = Element & { shadowRoot: ShadowRoot | null }

function getWidgetHost(): ShadowHost | null {
  return document.querySelector('[data-bigconnect-widget]') as ShadowHost | null
}

/** Open the widget's call panel by clicking its (hidden) launcher. */
function openWidgetPanel(host: ShadowHost): boolean {
  const root = host.shadowRoot
  if (!root) return false
  const panel = root.querySelector('.panel') as HTMLElement | null
  const launcher = root.querySelector('.launcher') as HTMLElement | null
  if (!launcher) return false
  // If the panel is already open, nothing to do.
  if (panel && !panel.hidden) return true
  launcher.click()
  return true
}

/** Hide the widget's own launcher so only our branded button shows. */
function hideWidgetLauncher(root: ShadowRoot): void {
  if (root.querySelector('style[data-bdg-hide-launcher]')) return
  const style = document.createElement('style')
  style.setAttribute('data-bdg-hide-launcher', '')
  style.textContent = '.launcher { display: none !important; }'
  root.appendChild(style)
}

function CallWidget() {
  const [isLoading, setIsLoading] = useState(false)

  const handleOpenWidget = () => {
    // Widget already loaded — just open its panel.
    const existing = getWidgetHost()
    if (existing) {
      openWidgetPanel(existing)
      return
    }

    if (document.getElementById(WIDGET_SCRIPT_ID) || isLoading) return

    setIsLoading(true)

    const script = document.createElement('script')
    script.id = WIDGET_SCRIPT_ID
    script.type = 'module'
    script.src = WIDGET_SRC
    script.dataset.widgetKey = WIDGET_KEY
    script.dataset.title = 'BIGDATA GHANA'
    script.dataset.subtitle = 'Speak to our assistant, free'
    script.dataset.position = 'bottom-left'
    script.dataset.color = '#1E8A00'
    document.body.appendChild(script)

    // Poll for the widget to mount, then hide its launcher and open the panel.
    let tries = 0
    const interval = setInterval(() => {
      tries += 1
      const host = getWidgetHost()
      if (host?.shadowRoot) {
        hideWidgetLauncher(host.shadowRoot)
        openWidgetPanel(host)
        setIsLoading(false)
        clearInterval(interval)
      } else if (tries > 100) {
        // ~10s with no mount: give up so the button isn't stuck spinning.
        setIsLoading(false)
        clearInterval(interval)
      }
    }, 100)
  }

  return (
    <button
      className="call-widget"
      onClick={handleOpenWidget}
      aria-label="Talk to us"
      aria-busy={isLoading}
    >
      <svg className="call-widget__ring-text" viewBox="0 0 100 100">
        <defs>
          <path id="callCirclePath" d="M 50,50 m -37,0 a 37,37 0 1,1 74,0 a 37,37 0 1,1 -74,0" />
          <linearGradient id="bdgGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#E3000F" />
            <stop offset="50%" stopColor="#F2A900" />
            <stop offset="100%" stopColor="#1E8A00" />
          </linearGradient>
        </defs>
        <text>
          <textPath href="#callCirclePath" className="call-widget__text-path">
            talk to us · talk to us · talk to us · 
          </textPath>
        </text>
      </svg>
      <div className="call-widget__avatar">
        {isLoading ? (
          <span className="call-widget__spinner" aria-hidden="true" />
        ) : (
          /* Phone Icon */
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="url(#bdgGradient)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="call-widget__icon">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/>
          </svg>
        )}
      </div>
    </button>
  )
}

export default CallWidget
